import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Building2, Lock, Unlock, CalendarPlus, BadgeCheck, Users, GraduationCap, FileSpreadsheet, Clock, CreditCard, Save, Trash2 } from "lucide-react";

const STATUS = { trial: ["Deneme", "bg-sky-500/15 text-sky-700 dark:text-sky-300"], active: ["Aktif", "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"], locked: ["Kilitli", "bg-rose-500/15 text-rose-700 dark:text-rose-300"] };

export default function Orgs() {
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState("");
  const [bank, setBank] = useState({ bank_name: "", iban: "", account_holder: "", description: "" });
  const load = () => api.get("/platform/orgs").then((r) => setOrgs(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); api.get("/platform/payment-info").then((r) => setBank(r.data)).catch(() => {}); }, []);

  const saveBank = async () => {
    try { const r = await api.put("/platform/payment-info", bank); setBank(r.data); toast.success("EFT bilgileri kaydedildi — kilitli kurumlar bu IBAN'ı görecek"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const act = async (o, action) => {
    const labels = { activate_monthly: "AYLIK aboneliği aktif et (1.500 ₺)", activate_yearly: "YILLIK aboneliği aktif et (18.000 ₺)", lock: "KİLİTLE", unlock: "kilidi aç", extend_trial: "denemeyi 7 gün uzat" };
    if (!window.confirm(`${o.name}: ${labels[action]}?`)) return;
    try { await api.patch(`/platform/orgs/${o.id}`, { action }); toast.success("Güncellendi"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (o) => {
    if (!window.confirm(`"${o.name}" kurumunu ve TÜM verilerini (kullanıcılar, öğrenciler, sınavlar) KALICI olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) return;
    try { await api.delete(`/platform/orgs/${o.id}`); toast.success("Kurum silindi"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const list = orgs.filter((o) => !q || o.name.toLowerCase().includes(q.toLowerCase()) || o.owner_email.includes(q.toLowerCase()));
  const fmt = (d) => d ? new Date(d).toLocaleDateString("tr-TR") : "—";
  const fmtDT = (d) => d ? new Date(d).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "henüz giriş yok";
  return (
    <div className="space-y-6" data-testid="orgs-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Building2 className="h-6 w-6 text-primary" />Kurumlar</h1>
        <Badge variant="outline" data-testid="orgs-count">{orgs.length} kurum</Badge>
        <Input placeholder="Kurum / e-posta ara" value={q} onChange={(e) => setQ(e.target.value)} className="ml-auto max-w-xs" data-testid="orgs-search" />
      </div>

      <Card className="p-4 border-border" data-testid="eft-settings-card">
        <div className="flex items-center gap-2 mb-1"><CreditCard className="h-5 w-5 text-primary" /><h2 className="font-bold">EFT / Havale Bilgileri</h2></div>
        <p className="text-xs text-muted-foreground mb-3">Buraya girdiğiniz hesap bilgileri, deneme/abonelik süresi dolan kurumların kilit ekranında gösterilir.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><label className="text-xs text-muted-foreground">Banka adı</label><Input value={bank.bank_name} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} placeholder="Örn. Ziraat Bankası" data-testid="eft-bank-name" /></div>
          <div><label className="text-xs text-muted-foreground">Hesap sahibi</label><Input value={bank.account_holder} onChange={(e) => setBank({ ...bank, account_holder: e.target.value })} placeholder="Ad Soyad / Ünvan" data-testid="eft-account-holder" /></div>
          <div className="sm:col-span-2"><label className="text-xs text-muted-foreground">IBAN</label><Input value={bank.iban} onChange={(e) => setBank({ ...bank, iban: e.target.value })} placeholder="TR00 0000 0000 0000 0000 0000 00" className="font-mono" data-testid="eft-iban" /></div>
          <div className="sm:col-span-2"><label className="text-xs text-muted-foreground">Açıklama (opsiyonel)</label><Input value={bank.description} onChange={(e) => setBank({ ...bank, description: e.target.value })} placeholder="Örn. Açıklamaya okul adınızı yazınız" data-testid="eft-description" /></div>
        </div>
        <Button className="mt-3" onClick={saveBank} data-testid="eft-save"><Save className="h-4 w-4 mr-1" />Kaydet</Button>
      </Card>

      <div className="grid gap-3">
        {list.map((o) => {
          const [label, cls] = STATUS[o.status] || STATUS.trial;
          const locked = o.locked;
          return (
            <Card key={o.id} className={`p-4 border-border ${locked ? "border-rose-400/60" : ""}`} data-testid={`org-card-${o.id}`}>
              <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-bold text-lg truncate">{o.name}</span>
                    <Badge className={cls} data-testid={`org-status-${o.id}`}>{label}{locked && o.status !== "locked" ? " · süresi doldu" : ""}</Badge>
                    {o.plan && <Badge variant="outline">{o.plan === "monthly" ? "Aylık" : "Yıllık"}</Badge>}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">{o.owner_email} · kayıt {fmt(o.created_at)}</div>
                  <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mt-2">
                    <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{o.user_count} kullanıcı</span>
                    <span className="flex items-center gap-1"><GraduationCap className="h-3.5 w-3.5" />{o.student_count} öğrenci</span>
                    <span className="flex items-center gap-1"><FileSpreadsheet className="h-3.5 w-3.5" />{o.exam_count} sınav</span>
                    <span>{o.status === "trial" ? `Deneme bitiş: ${fmt(o.trial_ends_at)}` : `Abonelik bitiş: ${o.paid_until ? fmt(o.paid_until) : "sınırsız"}`}{o.days_left != null ? ` (${o.days_left} gün)` : ""}</span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mt-1" data-testid={`org-activity-${o.id}`}>
                    <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />Son giriş: {fmtDT(o.last_login_at)}</span>
                    <span>Toplam giriş: {o.login_count || 0}</span>
                  </div>
                  {o.notes && <div className="text-xs mt-1 italic">{o.notes}</div>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => act(o, "activate_monthly")} data-testid={`org-activate-monthly-${o.id}`}><BadgeCheck className="h-4 w-4 mr-1" />Aylık</Button>
                  <Button size="sm" onClick={() => act(o, "activate_yearly")} data-testid={`org-activate-yearly-${o.id}`}><BadgeCheck className="h-4 w-4 mr-1" />Yıllık</Button>
                  <Button size="sm" variant="outline" onClick={() => act(o, "extend_trial")} data-testid={`org-extend-${o.id}`}><CalendarPlus className="h-4 w-4 mr-1" />+7 gün</Button>
                  {o.status === "locked" ? (
                    <Button size="sm" variant="outline" onClick={() => act(o, "unlock")} data-testid={`org-unlock-${o.id}`}><Unlock className="h-4 w-4 mr-1" />Aç</Button>
                  ) : (
                    <Button size="sm" variant="destructive" onClick={() => act(o, "lock")} data-testid={`org-lock-${o.id}`}><Lock className="h-4 w-4 mr-1" />Kilitle</Button>
                  )}
                  <Button size="sm" variant="outline" className="text-rose-600 border-rose-300 hover:bg-rose-50" onClick={() => del(o)} data-testid={`org-delete-${o.id}`}><Trash2 className="h-4 w-4 mr-1" />Sil</Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
